"use client";

import { useState } from "react";

const TIER_OPTIONS = [
  { value: "community_partner", label: "Community Partner" },
  { value: "featured_partner", label: "Featured Partner" },
  { value: "founding_partner", label: "Founding Partner" },
];

export function PartnershipForm() {
  const [form, setForm] = useState({
    brandName: "",
    name: "",
    email: "",
    website: "",
    tier: "featured_partner",
    message: "",
  });
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [errorMsg, setErrorMsg] = useState("");

  function update(field: string, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.brandName.trim() || !form.name.trim() || !form.email.trim()) {
      setErrorMsg("Please fill in all required fields.");
      return;
    }

    setStatus("loading");
    setErrorMsg("");

    try {
      const res = await fetch("/api/partnership-enquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          organisation: form.brandName,
          email: form.email,
          website: form.website || undefined,
          type: form.tier,
          message: form.message || undefined,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Something went wrong");
      }

      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err instanceof Error ? err.message : "Something went wrong"
      );
    }
  }

  if (status === "success") {
    return (
      <div className="bg-warm-bg rounded-[12px] p-8 text-center border border-border-light">
        <div className="w-14 h-14 rounded-full bg-gold/15 flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-7 h-7 text-gold"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        </div>
        <h3 className="text-navy text-xl font-bold mb-2">
          Application received
        </h3>
        <p className="text-text-muted text-[15px] leading-relaxed">
          Thanks for your interest. We&apos;ll review your application and get
          back to you within a few days.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label
          htmlFor="brandName"
          className="block text-navy text-[13px] font-semibold mb-1.5"
        >
          Brand name <span className="text-gold">*</span>
        </label>
        <input
          id="brandName"
          type="text"
          value={form.brandName}
          onChange={(e) => update("brandName", e.target.value)}
          placeholder="Your brand or company"
          className="w-full px-4 py-3 rounded-sm border border-border bg-white text-text-body text-[15px] placeholder:text-text-muted/50 focus:outline-none focus:border-gold transition-colors"
          required
        />
      </div>

      <div>
        <label
          htmlFor="contactName"
          className="block text-navy text-[13px] font-semibold mb-1.5"
        >
          Your name <span className="text-gold">*</span>
        </label>
        <input
          id="contactName"
          type="text"
          value={form.name}
          onChange={(e) => update("name", e.target.value)}
          placeholder="Full name"
          className="w-full px-4 py-3 rounded-sm border border-border bg-white text-text-body text-[15px] placeholder:text-text-muted/50 focus:outline-none focus:border-gold transition-colors"
          required
        />
      </div>

      <div>
        <label
          htmlFor="partnerEmail"
          className="block text-navy text-[13px] font-semibold mb-1.5"
        >
          Email <span className="text-gold">*</span>
        </label>
        <input
          id="partnerEmail"
          type="email"
          value={form.email}
          onChange={(e) => update("email", e.target.value)}
          placeholder="you@brand.com"
          className="w-full px-4 py-3 rounded-sm border border-border bg-white text-text-body text-[15px] placeholder:text-text-muted/50 focus:outline-none focus:border-gold transition-colors"
          required
        />
      </div>

      <div>
        <label
          htmlFor="website"
          className="block text-navy text-[13px] font-semibold mb-1.5"
        >
          Website{" "}
          <span className="text-text-muted font-normal">(optional)</span>
        </label>
        <input
          id="website"
          type="url"
          value={form.website}
          onChange={(e) => update("website", e.target.value)}
          placeholder="https://yourbrand.com"
          className="w-full px-4 py-3 rounded-sm border border-border bg-white text-text-body text-[15px] placeholder:text-text-muted/50 focus:outline-none focus:border-gold transition-colors"
        />
      </div>

      <div>
        <label className="block text-navy text-[13px] font-semibold mb-2.5">
          Partnership tier <span className="text-gold">*</span>
        </label>
        <div className="space-y-2.5">
          {TIER_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`flex items-center gap-3 px-4 py-3 rounded-sm border cursor-pointer transition-colors ${
                form.tier === opt.value
                  ? "border-gold bg-gold/5"
                  : "border-border hover:border-gold/40"
              }`}
            >
              <input
                type="radio"
                name="tier"
                value={opt.value}
                checked={form.tier === opt.value}
                onChange={(e) => update("tier", e.target.value)}
                className="accent-gold w-4 h-4"
              />
              <span className="text-text-body text-[14px]">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label
          htmlFor="partnerMessage"
          className="block text-navy text-[13px] font-semibold mb-1.5"
        >
          Message{" "}
          <span className="text-text-muted font-normal">(optional)</span>
        </label>
        <textarea
          id="partnerMessage"
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          placeholder="Tell us a bit about your brand and what you're looking for..."
          rows={4}
          className="w-full px-4 py-3 rounded-sm border border-border bg-white text-text-body text-[15px] placeholder:text-text-muted/50 focus:outline-none focus:border-gold transition-colors resize-y"
        />
      </div>

      {errorMsg && <p className="text-red-600 text-[13px]">{errorMsg}</p>}

      <button
        type="submit"
        disabled={status === "loading"}
        className="w-full bg-gold text-navy px-8 py-3.5 rounded-sm text-[15px] font-semibold font-sans transition-all hover:bg-gold-light min-h-[44px] cursor-pointer border-0 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {status === "loading" ? "Submitting..." : "Submit application"}
      </button>
    </form>
  );
}
